// ============================================================================
// 바인더 저장소 — localStorage. 읽기는 관대하게, 쓰기는 엄격하게 (BINDER_DESIGN §4)
//
// localStorage 접근은 loadBinder / saveBinder 두 함수에만 있다. 나머지는 순수 함수라
// 브라우저 없이 tools/binder-save.mjs 로 검증한다.
//
// 저장 키는 카드 **id** 다. 슬롯은 표시 위치일 뿐이라 슬롯을 전부 재배정해도
// (A→B 세트 구역 전환, --reslot) 저장 데이터는 손댈 필요가 없다.
// ============================================================================
import { faceCount } from './cards.ts';
import { P } from './params.ts';

// **키는 v1 그대로 두고 안의 v 만 올린다.** 키를 바꾸면 이미 도감을 모은 사람의 데이터가 통째로 사라진다.
export const SAVE_KEY = 'cr.binder.v1';
export const CURRENT_V = 2;

/**
 * v2 = v1 + 지갑. **소지금과 카드가 한 덩어리인 이유**: 판매는 "카드 -1, 돈 +N" 이 한 번에
 * 일어나야 한다. 키를 나누면 한쪽 쓰기만 성공했을 때(용량 초과·탭 종료) 카드가 사라지거나
 * 돈이 두 배가 된다. 같은 키 = 한 번의 setItem = 원자적이다.
 */
export interface BinderSaveV1 {
  v: number;
  updated: string; // ISO 시각. 진단용 — 로직에 쓰지 않는다
  cards: Record<string, number>; // card id → 수량. 0 이하는 항목 자체를 두지 않는다
  packs: number; // 지금까지 수납된 팩 수
  lastFace: number; // 마지막으로 보던 면 1..F
  // ---- v2
  money: number; // 소지금. **센트 정수**다 (달러 아님) — 0.05 누적의 부동소수 오차를 피한다
  openPaid: boolean; // 값을 치른 미개봉 팩이 개봉 화면에 있는가. 리로드를 넘어 유지된다
}

function startCents(): number {
  return Math.round(P.econ.startMoney * 100);
}

export function emptySave(): BinderSaveV1 {
  return { v: CURRENT_V, updated: new Date().toISOString(), cards: {}, packs: 0, lastFace: 1, money: startCents(), openPaid: false };
}

// 스키마 **모양**이 바뀔 때만 필요하다. 카드 추가·세트 구역 전환·면 수 변화에는 필요 없다.
const MIGRATIONS: Record<number, (old: any) => any> = {
  // 경제 도입. 이미 도감을 모은 사람은 시작 소지금을 그대로 받는다 (팩을 이미 산 것으로 치지 않는다).
  2: (s) => ({ ...s, v: 2, money: startCents(), openPaid: false }),
};

export function migrate(obj: any): BinderSaveV1 | null {
  let cur = obj;
  let guard = 0;
  while (cur && cur.v < CURRENT_V && MIGRATIONS[cur.v + 1] && guard++ < 32) cur = MIGRATIONS[cur.v + 1](cur);
  return cur && cur.v === CURRENT_V ? cur : null;
}

/** 읽기 결과. future=true 면 모르는 미래 버전이라 **쓰지 않는다**. */
export interface ParseResult {
  save: BinderSaveV1;
  future: boolean;
  corrupt: boolean;
}

/** 문자열 → 저장소. 절대 throw 하지 않는다. */
export function parseSave(text: string | null): ParseResult {
  const empty = emptySave();
  if (text === null || text === undefined || text === '') return { save: empty, future: false, corrupt: false };
  let obj: any;
  try {
    obj = JSON.parse(text);
  } catch {
    return { save: empty, future: false, corrupt: true };
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return { save: empty, future: false, corrupt: true };

  const v = Number(obj.v);
  if (!Number.isFinite(v)) return { save: empty, future: false, corrupt: true };
  if (v > CURRENT_V) return { save: empty, future: true, corrupt: false }; // 읽지도 쓰지도 않는다
  if (v < CURRENT_V) {
    const m = migrate(obj);
    if (!m) return { save: empty, future: false, corrupt: true };
    obj = m;
  }

  const cards: Record<string, number> = {};
  const src = obj.cards;
  if (src && typeof src === 'object' && !Array.isArray(src)) {
    for (const k of Object.keys(src)) {
      const n = src[k];
      // 양의 정수만. **CARD_DEFS 에 없는 id 도 보존한다** — 나중에 정의되면 살아난다.
      if (typeof n === 'number' && Number.isFinite(n) && Number.isInteger(n) && n > 0) cards[k] = n;
    }
  }
  const packs = Number.isFinite(obj.packs) && obj.packs >= 0 ? Math.floor(obj.packs) : 0;
  // 소지금. **부정 방지가 목적이 아니다** — localStorage 는 어차피 사용자가 고칠 수 있다.
  // 목적은 NaN·음수가 게임 안으로 퍼지지 않게 하는 것뿐이라, 이상하면 시작 소지금으로 되돌린다.
  const rawMoney = Number(obj.money);
  const money = Number.isFinite(rawMoney) && Number.isInteger(rawMoney) && rawMoney >= 0 ? rawMoney : startCents();
  const openPaid = obj.openPaid === true;
  const F = faceCount();
  let lastFace = Math.floor(Number(obj.lastFace));
  if (!Number.isFinite(lastFace)) lastFace = 1;
  lastFace = Math.max(1, Math.min(F, lastFace));

  return {
    save: { v: CURRENT_V, updated: typeof obj.updated === 'string' ? obj.updated : empty.updated, cards, packs, lastFace, money, openPaid },
    future: false,
    corrupt: false,
  };
}

export function serializeSave(s: BinderSaveV1): string {
  return JSON.stringify({ v: CURRENT_V, updated: new Date().toISOString(), cards: s.cards, packs: s.packs, lastFace: s.lastFace, money: s.money, openPaid: s.openPaid });
}

/** 팩 커밋. ids 안의 null(정의가 없는 등급)은 무시한다. 반환 = 실제로 더해진 장수. */
export function addCards(s: BinderSaveV1, ids: readonly (string | null)[]): number {
  let n = 0;
  for (const id of ids) {
    if (!id) continue;
    s.cards[id] = (s.cards[id] ?? 0) + 1;
    n++;
  }
  s.packs++;
  return n;
}

export function totalOwned(s: BinderSaveV1): number {
  let n = 0;
  for (const k of Object.keys(s.cards)) n += s.cards[k];
  return n;
}

// ---------------------------------------------------------------- localStorage
let writeLocked = false;

function ls(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

export function loadBinder(): BinderSaveV1 {
  const st = ls();
  if (!st) return emptySave();
  let text: string | null = null;
  try {
    text = st.getItem(SAVE_KEY);
  } catch {
    return emptySave();
  }
  const r = parseSave(text);
  if (r.corrupt && text) {
    // 원본은 덮어쓰지 않는다 — 옆으로 옮겨 둔다
    try {
      st.setItem(`${SAVE_KEY}.corrupt.${Date.now()}`, text);
      st.removeItem(SAVE_KEY);
    } catch {
      /* 용량 초과 등은 무시 */
    }
    console.warn('[save] 저장 데이터가 깨졌다 — .corrupt 키로 옮기고 빈 저장소로 시작한다');
  }
  writeLocked = r.future;
  if (r.future) console.warn('[save] 모르는 미래 버전의 저장 데이터다 — 읽지도 쓰지도 않는다');
  return r.save;
}

export function saveBinder(s: BinderSaveV1): void {
  if (writeLocked) return;
  const st = ls();
  if (!st) return;
  try {
    st.setItem(SAVE_KEY, serializeSave(s));
  } catch (e) {
    console.warn('[save] 저장 실패 (용량 초과 / 프라이빗 모드?)', e);
  }
}

export function clearBinder(): void {
  const st = ls();
  writeLocked = false;
  if (!st) return;
  try {
    st.removeItem(SAVE_KEY);
  } catch {
    /* ignore */
  }
}

export function isWriteLocked(): boolean {
  return writeLocked;
}
